import { z } from 'zod'
import { makeOptional } from '@/shared/lib/helpers/schema.helpers'
import { phoneSchema, dateSchema, optionalPhoneSchema } from '@/shared/lib/schemas/common.schema'
import { genderOptions, tShirtSizeOptions } from '@/features/profile/schemas/profile-form.schema'
import { selectSchema } from '@/shared/lib/schemas/common.schema'
import { otherLanguageOptions } from '@/features/auth/constants/select-options'

const experienceRatingSchema = z.union([z.number(), z.string()]).transform((val) => {
    if (!val || val === '') return undefined
    return typeof val === 'string' ? parseInt(val, 10) : val
}).refine((val) => val === undefined || (val >= 1 && val <= 5), 'Experience must be between 1 and 5').optional()

export const updateUserFormSchema = z.object({
    fullname: z.string().optional(),
    email: z.string().optional().refine((val) => !val || val === '' || z.string().email().safeParse(val).success, "Please enter a valid email address"),
    organizationName: z.string().optional(),
    preferredLocation: z.string().optional(),
    profilePicture: z.union([z.instanceof(File), z.string()]).optional(),
    gender: makeOptional(selectSchema(genderOptions)),
    // Coerce empty/invalid/legacy values so backend strings (e.g. "Medium", "S") don't cause "Expected null, received string"
    tShirtSize: z.preprocess(
        (val) => {
            if (val === null || val === undefined || val === '') return undefined
            const s = typeof val === 'string' ? val.trim().toLowerCase() : val
            if (s === '' || typeof s !== 'string') return undefined
            const validValues = tShirtSizeOptions.map((o) => o.value)
            if (validValues.includes(s as (typeof validValues)[number])) return s
            // Map common legacy values to enum
            const legacy: Record<string, (typeof validValues)[number]> = {
                s: 'small', m: 'medium', l: 'large', xl: 'extra_large', '2xl': '2x_large', '2x': '2x_large',
                medium: 'medium', large: 'large', small: 'small', extra_large: 'extra_large', '2x_large': '2x_large',
            }
            return legacy[s] ?? undefined
        },
        z.enum(tShirtSizeOptions.map((o) => o.value) as [string, ...string[]]).optional()
    ),
    /** Volunteers table; date-only (stored as date in DB) */
    backgroundCheck: makeOptional(dateSchema, "date"),
    passportNumber: z.preprocess(
        (val) => (val === null ? undefined : val),
        z.string().max(100).optional()
    ),
    emergencyContact1FullName: z.string().max(200).optional(),
    emergencyContact1Relationship: z.string().max(100).optional(),
    emergencyContact1Phone: optionalPhoneSchema,
    emergencyContact2FullName: z.string().max(200).optional(),
    emergencyContact2Relationship: z.string().max(100).optional(),
    emergencyContact2Phone: optionalPhoneSchema,
    healthInsuranceProvider: z.string().max(200).optional(),
    healthInsurancePhone: optionalPhoneSchema,
    healthInsurancePolicyNumber: z.string().max(100).optional(),
    travelInsuranceProvider: z.string().max(200).optional(),
    travelInsurancePhone: optionalPhoneSchema,
    travelInsurancePolicyNumber: z.string().max(100).optional(),
    jamaicaC5ApplicationComplete: z.boolean().optional(),
    jamaicaTripFlightDetails: z.string().max(4000).optional(),
    healthConditions: z.string().max(1000).optional(),
    dietaryRestrictions: z.string().max(500).optional(),
    vehicleMake: z.string().max(100).optional(),
    vehicleModel: z.string().max(100).optional(),
    vehicleColor: z.string().max(100).optional(),
    vehicleLicensePlateNumber: z.string().max(50).optional(),
    vehicleLicensePlateState: z.string().max(50).optional(),
    selectedRegion: z.union([z.number(), z.string()]).transform((val) => {
        if (!val || val === '') return undefined;
        return typeof val === 'string' ? parseInt(val, 10) : val;
    }).optional(),
    selectedRegions: z.union([
        z.array(z.string()),
        z.array(z.number()),
    ]).transform((val) => {
        if (!Array.isArray(val) || val.length === 0) return undefined;
        return val.map((v) => (typeof v === 'string' ? parseInt(v, 10) : v)).filter((n) => !isNaN(n));
    }).optional(),
    // Phone is optional on this form – empty / undefined are allowed, otherwise it must be a valid number
    // and will be transformed to E.164 (e.g. 7199648627 -> +17199648627), matching the Profile phone behavior
    phone: optionalPhoneSchema,
    // FormSelectInput coerces digit-only option values to number (shared behavior for region ids).
    // Role options use "1"–"4"; normalize so Zod accepts both string and number from RHF.
    role: z.preprocess(
        (val) => {
            if (val === undefined || val === null || val === '') return undefined
            return String(val)
        },
        z.string().optional()
    ),
    status: z.union([z.boolean(), z.enum(['Active', 'Inactive'])]).transform((val) => {
        if (typeof val === 'boolean') return val;
        if (val === 'Active') return true;
        if (val === 'Inactive') return false;
        return undefined;
    }).optional(),
    // Date of birth matches Profile behavior: optional date (sent as Date from the form)
    dateOfBirth: makeOptional(dateSchema, "date"),
    experienceConstruction: experienceRatingSchema,
    experienceDataManagement: experienceRatingSchema,
    experienceStorytelling: experienceRatingSchema,
    mealPreparationAbility: experienceRatingSchema,
    experienceLeadingTeams: experienceRatingSchema,
    experienceDrywallFinishing: experienceRatingSchema,
    experienceRoughCarpentry: experienceRatingSchema,
    experienceFinishCarpentry: experienceRatingSchema,
    experienceFlooring: experienceRatingSchema,
    experienceWoodWorking: experienceRatingSchema,
    experienceElectrical: experienceRatingSchema,
    experiencePlumbing: experienceRatingSchema,
    experienceHvac: experienceRatingSchema,
    experienceArborist: experienceRatingSchema,
    experienceRoofing: experienceRatingSchema,
    experienceHeavyEquipment: experienceRatingSchema,
    experienceManualLabor: experienceRatingSchema,
    experienceAltMinistries: experienceRatingSchema,
    skillsAndQualities: z.string().optional(),
    // Accept any string or empty; coerce invalid/empty to undefined so legacy values (e.g. "English") don't cause "Expected null, received string"
    otherLanguages: z.preprocess(
        (val) => {
            if (val === null || val === undefined || val === '') return undefined
            const s = typeof val === 'string' ? val.trim() : val
            if (s === '') return undefined
            const validValues = otherLanguageOptions.map((o) => o.value)
            return validValues.includes(s as (typeof validValues)[number]) ? s : undefined
        },
        z.string().optional()
    ),
    hasServedBefore: z.string().optional(),
    isChristFollower: z.string().optional(),
    chatNotifications: z.boolean().optional(),
    workOrderNotifications: z.boolean().optional(),
    emailUpdates: z.boolean().optional(),
    servePeriods: z.array(z.object({
        from: z.any().transform((val) => {
            if (!val || val === undefined || val === null || val === '') return undefined;
            if (val instanceof Date) return val;
            if (typeof val === 'string') {
                const parsed = new Date(val);
                return isNaN(parsed.getTime()) ? undefined : parsed;
            }
            return undefined;
        }).pipe(z.union([z.date(), z.undefined()])),
        to: z.any().transform((val) => {
            if (!val || val === undefined || val === null || val === '') return undefined;
            if (val instanceof Date) return val;
            if (typeof val === 'string') {
                const parsed = new Date(val);
                return isNaN(parsed.getTime()) ? undefined : parsed;
            }
            return undefined;
        }).pipe(z.union([z.date(), z.undefined()])),
        regionId: z.union([z.number(), z.string()]).transform((val) => {
            if (!val || val === undefined || val === '' || val === null) return undefined;
            return typeof val === 'string' ? parseInt(val, 10) : val;
        }).optional(),
    })).optional(),
    arrivalDate: makeOptional(dateSchema),
    departureDate: makeOptional(dateSchema),
})

// Form input type (before transformation) - allows status as string
export type UpdateUserFormInputType = {
    fullname?: string;
    email?: string;
    organizationName?: string;
    profilePicture?: File | string;
    preferredLocation?: string;
    gender?: string;
    tShirtSize?: string;
    backgroundCheck?: Date | null;
    passportNumber?: string;
    emergencyContact1FullName?: string;
    emergencyContact1Relationship?: string;
    emergencyContact1Phone?: string;
    emergencyContact2FullName?: string;
    emergencyContact2Relationship?: string;
    emergencyContact2Phone?: string;
    healthInsuranceProvider?: string;
    healthInsurancePhone?: string;
    healthInsurancePolicyNumber?: string;
    travelInsuranceProvider?: string;
    travelInsurancePhone?: string;
    travelInsurancePolicyNumber?: string;
    jamaicaC5ApplicationComplete?: boolean;
    jamaicaTripFlightDetails?: string;
    healthConditions?: string;
    dietaryRestrictions?: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleColor?: string;
    vehicleLicensePlateNumber?: string;
    vehicleLicensePlateState?: string;
    selectedRegion?: number | string;
    selectedRegions?: string[];
    phone?: string | null;
    /** RHF may hold string (defaults) or number after FormSelectInput coerces digit option values */
    role?: string | number;
    status?: 'Active' | 'Inactive' | boolean;
    dateOfBirth?: Date | null;
    experienceConstruction?: number | string;
    experienceDataManagement?: number | string;
    experienceStorytelling?: number | string;
    mealPreparationAbility?: number | string;
    experienceLeadingTeams?: number | string;
    experienceDrywallFinishing?: number | string;
    experienceRoughCarpentry?: number | string;
    experienceFinishCarpentry?: number | string;
    experienceFlooring?: number | string;
    experienceWoodWorking?: number | string;
    experienceElectrical?: number | string;
    experiencePlumbing?: number | string;
    experienceHvac?: number | string;
    experienceArborist?: number | string;
    experienceRoofing?: number | string;
    experienceHeavyEquipment?: number | string;
    experienceManualLabor?: number | string;
    experienceAltMinistries?: number | string;
    skillsAndQualities?: string;
    otherLanguages?: string;
    hasServedBefore?: string;
    isChristFollower?: string;
    chatNotifications?: boolean;
    workOrderNotifications?: boolean;
    emailUpdates?: boolean;
    servePeriods?: Array<{ from?: Date; to?: Date; regionId?: number | string }>;
    arrivalDate?: Date | null;
    departureDate?: Date | null;
}

// Form output type (after transformation) - status is boolean
export type UpdateUserFormType = z.infer<typeof updateUserFormSchema>
